/**
 * Action Buttons — the estimate-results "Next Steps" card.
 *
 * The bar is now CONFIG-DRIVEN: it renders whatever ActionButtonConfig[] the
 * loaded retailer resolved to (universal default → per-retailer override,
 * delivered in the products payload and read via getActionButtons()). Each
 * button's behavior is dispatched by its `action.type`. When no config is
 * available (pre-migration / offline) it falls back to DEFAULT_ACTION_BUTTONS,
 * which reproduces the original four buttons exactly.
 */

import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useEstimator } from '../../context/EstimatorContext';
import { usePartnerBranding } from '../../context/PartnerBrandingContext';
import { downloadEstimatePDF, PDFExtraData, TrimOpening } from '../../utils/pdf';
import { trackActionButtonClick, trackFindRetailerClick, trackPdfSaved, trackResultsPrinted } from '../../utils/gtmTracking';
import { getActionButtons, getLoadedRetailerId, getOutputSections } from '../../data/productTable';
import { MapModal } from '../maps/MapModal';
import { DEFAULT_ACTION_BUTTONS, ActionButtonConfig, ActionButtonVariant, ActionSpec } from '../../config/actionButtons';
import { DynamicActionIcon } from './DynamicActionIcon';
import { ActionButtonModal } from './ActionButtonModal';
import { calculationModeLabel, spaceTypeLabel } from './configDataPoints';
import './ActionButtons.css';

const VARIANT_CLASS: Record<ActionButtonVariant, string> = {
  default: '',
  primary: 'btn-primary',
  secondary: 'btn-secondary',
};

interface ActionButtonsProps {
  estimateId?: string;
  trimOpenings?: TrimOpening[];
  userNotes?: string;
  /** Pass estimate directly (e.g. from shared estimate page) to bypass context */
  estimate?: import('../../types').EstimateOutput;
  /** Override currency when not using context */
  overrideCurrency?: import('../../types').Currency;
  /** Show per-line prices in the PDF (mirrors the on-screen BOM). */
  showPrices?: boolean;
  /** Wizard state to use for the PDF (e.g. the shared estimate's loaded state).
   *  Falls back to the live EstimatorContext state when omitted. */
  wizardState?: import('../../types').WizardState;
  /** Estimated-total format for the PDF. Only shared/submitted estimates pass
   *  the retailer's 'precise' setting; the live wizard stays 'rounded'. */
  totalFormat?: import('../../types').TotalFormat;
  /** Saved/shared estimate name — used as the PDF title (shared estimate page). */
  estimateName?: string;
  /** ISO pricing date (saved/shared estimates) — adds the 30-day validity note
   *  to the PDF disclaimer. Omitted on the live wizard. */
  pricedAt?: string | null;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({ estimateId, trimOpenings, userNotes, estimate, overrideCurrency, showPrices = false, wizardState, totalFormat = 'rounded', estimateName, pricedAt }) => {
  const { t } = useTranslation('results');
  const { state } = useEstimator();
  // Active partner branding (portal in the wizard, or the estimate's origin
  // retailer on a shared link) → branded PDF colors + logo.
  const { partner } = usePartnerBranding();
  const estimateResult = estimate || state.estimateResult;
  // Prefer an explicitly-provided wizard state (shared estimate page) over the
  // live context, which holds the active wizard session — not the loaded share.
  const pdfWizardState = wizardState || state;
  const currency = overrideCurrency || state.currency;
  const { wallsConfig, ceilingConfig, applicationConfig, calculationMode } = state;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [modal, setModal] = useState<Extract<ActionSpec, { type: 'modal' }> | null>(null);
  const [mapModal, setMapModal] = useState<Extract<ActionSpec, { type: 'map' }> | null>(null);

  // Resolved bar for the loaded retailer; fall back to the built-in defaults so
  // the card is never empty (pre-migration / offline / fallback product data).
  const resolved = getActionButtons();
  const buttons = (resolved.length > 0 ? resolved : DEFAULT_ACTION_BUTTONS)
    .filter(b => b.enabled)
    .filter(b => !b.requiresSavedEstimate || !!estimateId)
    .slice()
    .sort((a, b) => a.order - b.order);

  const buildExtraData = (): PDFExtraData => {
    const spaceType = spaceTypeLabel(applicationConfig.spaceType);
    // Project type from the estimate's own input record (the live context is
    // stale on shared-estimate pages and after mode changes).
    const pdfMode = estimateResult?.input?.calculationMode || calculationMode;
    const projectType = calculationModeLabel(pdfMode);
    return {
      trimOpenings,
      userNotes: userNotes || estimateResult?.userNotes,
      estimateName,
      showPrices,
      wizardState: pdfWizardState,
      totalFormat,
      pricedAt,
      partnerTheme: partner?.theme ?? null,
      partnerLogoUrl: partner?.logoUrl ?? null,
      partnerName: partner?.name ?? null,
      outputSections: getOutputSections(),
      spaceType,
      projectType,
      wallColor: wallsConfig.color,
      wallOrientation: wallsConfig.orientation,
      ceilingColor: ceilingConfig.include ? ceilingConfig.color : undefined,
      slatWallConfig: wallsConfig.slatWallNumPanels > 0 ? {
        color: wallsConfig.slatWallColor,
        numPanels: wallsConfig.slatWallNumPanels,
        startHeight: wallsConfig.slatWallStartHeight,
      } : undefined,
      garageConfig: applicationConfig.garage?.enabled && applicationConfig.garage?.numDoors > 0 ? {
        numDoors: applicationConfig.garage.numDoors,
        width: applicationConfig.garage.width,
        height: applicationConfig.garage.height,
      } : undefined,
    };
  };

  const doPdf = async (btnId: string) => {
    if (!estimateResult || busyId) return;
    trackPdfSaved(calculationMode, estimateResult);
    setBusyId(btnId);
    try {
      await downloadEstimatePDF(estimateResult, currency, buildExtraData());
    } catch (err) {
      console.error('Failed to download PDF:', err);
    } finally {
      setBusyId(null);
    }
  };

  const doPrint = () => {
    if (estimateResult) trackResultsPrinted(calculationMode, estimateResult);
    window.print();
  };

  const scrollToComments = () => {
    document.getElementById('estimate-comments')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openLink = (btn: ActionButtonConfig, url: string, target: '_self' | '_blank') => {
    // Preserve the original Find-a-Retailer analytics event for that default button.
    if (btn.id === 'find-retailer') {
      trackFindRetailerClick(calculationMode, applicationConfig.spaceType, estimateResult ?? null, { buttonId: btn.id });
    }
    if (target === '_blank') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }
  };

  const runAction = (btn: ActionButtonConfig) => {
    const action = btn.action;
    // One generic event for every admin-configured button — id, type and label
    // are the retailer's own config, so a new retailer's buttons are tracked
    // with no code change. Specific events (pdf/print/find-retailer) still fire.
    trackActionButtonClick(calculationMode, applicationConfig.spaceType, estimateResult ?? null, {
      id: btn.id,
      actionType: action.type,
      label: btn.labelKey ? t(btn.labelKey) : btn.label,
      url: action.type === 'link' ? action.url : null,
      mapId: action.type === 'map' ? action.mapId : null,
    });
    switch (action.type) {
      case 'pdf':      void doPdf(btn.id); break;
      case 'print':    doPrint(); break;
      case 'comments': scrollToComments(); break;
      case 'link':     openLink(btn, action.url, action.target); break;
      case 'modal':    setModal(action); break;
      case 'map':
        // A retailer's store locator IS "find a retailer" — same event Gerson's
        // GTM tag already matches, regardless of which retailer configured it.
        trackFindRetailerClick(calculationMode, applicationConfig.spaceType, estimateResult ?? null, {
          buttonId: btn.id,
          mapId: action.mapId,
        });
        setMapModal(action);
        break;
      case 'ajax':
        // Phase 2 (server-side proxy + allowlist). Rendered but not yet wired.
        console.warn('[ActionButtons] ajax action is not available yet:', action.endpointId);
        break;
    }
  };

  return (
    <div className="action-buttons">
      <h3 className="action-title">{t('actions.title')}</h3>

      <div className="action-list">
        {buttons.map(btn => {
          const isPdf = btn.action.type === 'pdf';
          const busy = busyId === btn.id;
          const disabled = busy || (isPdf && !estimateResult);
          return (
            <button
              key={btn.id}
              type="button"
              className={`action-btn ${VARIANT_CLASS[btn.variant]} ${busy ? 'loading' : ''}`.trim()}
              onClick={() => runAction(btn)}
              disabled={disabled}
            >
              <span className="action-icon">
                <DynamicActionIcon icon={btn.icon} size={22} />
              </span>
              {/* A retailer's own label is database text and renders as
                  authored; only our built-in defaults carry a key. */}
              <span className="action-text">
                {busy ? t('actions.generating') : btn.labelKey ? t(btn.labelKey) : btn.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Share Info */}
      {estimateId && (
        <div className="share-info">
          <p className="share-note">
            {t('actions.shareNote')}
          </p>
        </div>
      )}

      {modal && (
        <ActionButtonModal
          title={modal.title}
          contentHtml={modal.contentHtml}
          size={modal.size}
          headerBg={modal.headerBg}
          headerFg={modal.headerFg}
          onClose={() => setModal(null)}
        />
      )}
      {mapModal && (
        <MapModal
          spec={mapModal}
          estimateGuid={estimateId ?? null}
          retailerId={partner?.id ?? getLoadedRetailerId()}
          onClose={() => setMapModal(null)}
        />
      )}
    </div>
  );
};

export default ActionButtons;
